<!-- The card's own control: folds the whole summary column away to a strip, leaving the list beside
     it the full width. A bare glyph rather than a button - it sits on the card's header line, and a
     large p-button's padding pushes that line out of square with the title beside it. -->
<ng-template #detailsToggle>
  <button type="button"
          class="shrink-0 p-1 -m-1 leading-none text-surface-900 hover:text-primary-600 cursor-pointer"
          [attr.aria-label]="toggleLabel()"
          [pTooltip]="toggleLabel()"
          tooltipPosition="left"
          (click)="toggleDetails()">
    <i class="fa-regular fa-sidebar-flip text-xl"></i>
  </button>
</ng-template>

<!-- Prepayment payment day (CLC-153): orange once today is past it. -->
<ng-template #scheduledPaymentTag>
  @if (scheduledPaymentDate()) {
    <p-tag icon="fa-regular fa-calendar" [severity]="prePaymentOverdue() ? 'warn' : 'info'"
           [value]="'Pay by ' + (scheduledPaymentDate() | date: 'dd/MM/yy')"></p-tag>
  }
</ng-template>

@if (!showDetails()) {
  @if (selectedTa() === undefined) {
    <p-card>
      <ng-container [ngTemplateOutlet]="detailsToggle"></ng-container>
    </p-card>
  } @else {
    <div class="w-full self-stretch p-5 bg-primary-50 rounded-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start gap-5 overflow-hidden">
        <ng-container [ngTemplateOutlet]="detailsToggle"></ng-container>
    </div>
  }
} @else if (selectedTa() === undefined) {
  <p-card class="w-full">
    <div class="w-full h-full flex flex-col px-2">
      <div class="w-full flex justify-between items-center">
        <!-- No TA, so no chain either - the header names only what is actually below it. -->
        <div class="text-Surface-900 text-base font-semibold font-['Inter'] leading-5">TA Summary</div>
        <ng-container [ngTemplateOutlet]="detailsToggle"></ng-container>
      </div>
      <div class="w-full flex justify-center py-14">
        <div class="text-center text-datatable-row-color text-sm font-normal font-['Inter'] leading-5">Select View Summary or click on a row background to view a more detailed summary.</div>
      </div>
    </div>
  </p-card>
} @else {
  <!-- The card has no height of its own: with the comments open the chain inside it stretches to
       the bottom of the page and the card follows, and with them folded away it is back to the size
       of the summary. scrollHeight is the backstop for the case the two disagree - a summary too
       tall for the room - where the card stops at the same line and scrolls instead.
       24px clear of the bottom, which is the page's own p-6, so the card lines up with it. -->
  <div scrollHeight
       [bottomPadding]="24"
       class="w-full self-stretch p-5 bg-primary-50 rounded-2xl shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start gap-4 overflow-y-auto overflow-x-hidden min-w-[250px]">
    <div class="w-full h-full flex flex-col gap-4 px-2">

      <!-- The card's header. Names both sections, since either can be folded away to nothing. -->
      <div class="w-full flex justify-between items-center gap-3">
        <div class="text-Surface-900 text-lg font-semibold font-['Inter'] leading-6">TA Summary and Comments</div>
        <ng-container [ngTemplateOutlet]="detailsToggle"></ng-container>
      </div>
      <div class="w-full h-px bg-surface-300"></div>

      <!-- Summary. Folds the same way the chain below it does, so a user reading the conversation
           can put the figures away and give it the whole card. -->
      <div class="w-full flex flex-col">
        <div class="w-full flex items-center gap-3 pr-4">
          @if (isFlightOnly()) {
            <i class="fa-solid fa-plane shrink-0 text-Surface-900" aria-label="Flights Only TA"></i>
          }
          <div class="flex-1 min-w-0 text-Surface-900 text-base font-semibold font-['Inter'] leading-5">Summary - {{ selectedTa()?.taNumber + ' - ' + selectedTa()?.primaryTraveller?.details?.name }}</div>
          <!-- Same chevron the comment chain draws on its own heading, so the card's two sections
               fold by the same control. -->
          <button type="button"
                  class="shrink-0 w-5 h-5 rounded-full border border-surface-700 flex items-center justify-center text-[10px] text-surface-700 hover:bg-surface-100 cursor-pointer"
                  [attr.aria-label]="summaryToggleLabel()"
                  data-testid="ta-summary-toggle"
                  (click)="toggleSummary()">
            <i class="fa-regular fa-lg"
               [ngClass]="summaryOpen() ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>

        <!-- Same fold as the comment chain's: a grid row between 0fr and 1fr, so nothing has to be
             measured. inert keeps the folded buttons out of the tab order. The chain below is
             capped against the bottom of the window, so it is re-measured once this has settled. -->
        <div class="grid summary-fold"
             [ngClass]="summaryOpen() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
             (transitionend)="onSummaryFolded($event)">
          <div class="min-h-0 overflow-hidden" [attr.inert]="summaryOpen() ? null : ''">
            <div class="w-full flex flex-col gap-5 pt-4">

              <!-- Cost bubbles stacked right-aligned in the narrow column beside the detail grid. -->
              <div class="w-full flex flex-col gap-2 items-end">
                @switch (costView()) {
                  @case ('finalisation') {
                    <ta-cost-bubble title="Acquitted TA Value" [value]="totalTaValue()" severity="outlined"></ta-cost-bubble>
                    <ta-cost-bubble title="Finalisation Payment Due" [value]="paymentComponents().finalisationDue" severity="primary"></ta-cost-bubble>
                    <ta-cost-bubble title="Day Trip Component" [value]="paymentComponents().dayTrip" severity="purple"></ta-cost-bubble>
                  }
                  @case ('prepayment') {
                    <ta-cost-bubble title="Approved TA Value" [value]="totalTaValue()" severity="outlined"></ta-cost-bubble>
                    <ta-cost-bubble title="Prepayment Component" titleIcon="fa-regular fa-piggy-bank text-green-700" [value]="paymentComponents().prepayment" severity="success"></ta-cost-bubble>
                    <ng-container [ngTemplateOutlet]="scheduledPaymentTag"></ng-container>
                  }
                  @case ('overpaid') {
                    <ta-cost-bubble title="Acquitted TA Value" [value]="totalTaValue()" severity="outlined"></ta-cost-bubble>
                    <ta-cost-bubble title="Overpaid Amount" [value]="overpaymentMade()" severity="danger"></ta-cost-bubble>
                    <ng-container [ngTemplateOutlet]="scheduledPaymentTag"></ng-container>
                  }
                  @case ('dayTrip') {
                    <ta-cost-bubble title="Acquitted TA Value" [value]="totalTaValue()" severity="outlined"></ta-cost-bubble>
                    <ta-cost-bubble title="Day Trip Component" [value]="paymentComponents().dayTrip" severity="purple"></ta-cost-bubble>
                  }
                  @default {
                    @if (isPreApproval() || isApprovedPreAcquittal()) {
                      <ta-cost-bubble [title]="isPreApproval() ? 'Total TA Value' : 'Approved TA Value'" [value]="totalTaValue()" severity="outlined"></ta-cost-bubble>
                      @if (isPrePaid() || paymentComponents().dayTrip > 0) {
                        <div class="w-full h-[1px] bg-surface-200"></div>
                      }
                      @if (isPrePaid()) {
                        <ta-cost-bubble title="Prepayment Component" titleIcon="fa-regular fa-piggy-bank text-green-700" [value]="paymentComponents().prepayment" severity="success"></ta-cost-bubble>
                      }
                      @if (paymentComponents().dayTrip > 0) {
                        <ta-cost-bubble title="Day Trip Component" [value]="paymentComponents().dayTrip" severity="purple"></ta-cost-bubble>
                      }
                    } @else {
                      <ta-cost-bubble title="Approved TA Value" [value]="originalTaValue()" severity="outlined"></ta-cost-bubble>
                      <ta-cost-bubble [title]="currentValueTitle()" [value]="currentTaValue()" severity="outlined"></ta-cost-bubble>
                      <ta-cost-bubble title="Change in TA Value" [value]="acquittedValueChange()" [severity]="acquittedValueChange() === 0 ? 'text' : 'outlined'" [signed]="true"></ta-cost-bubble>
                    }
                  }
                }
              </div>

              <div class="w-full flex gap-4 px-4">
                <div class="flex flex-col gap-2">
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Project</div>
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Purpose</div>
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Primary Traveller</div>
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Trip Dates</div>
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Trip Duration</div>
                  <div class="text-left text-datatable-row-color text-xs font-semibold font-['Inter']">Main Destination</div>
                </div>
                <div class="flex flex-col gap-2">
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{selectedTa()?.projectCode ?? '-'}}</div>
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{selectedTa()?.purpose ?? '-'}}</div>
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{selectedTa()?.primaryTraveller?.details?.name ?? '-'}}</div>
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{(selectedTa()?.startDate ? (selectedTa()?.startDate | date: 'dd/MM/yy') : 'No start date') + ' - ' + (selectedTa()?.endDate ? (selectedTa()?.endDate | date: 'dd/MM/yy') : 'No end date')}}</div>
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{getDuration()}}</div>
                  <div class="text-datatable-row-color text-xs font-normal font-['Inter']">{{selectedTa()?.mainDestinationLocationName ?? '-'}}</div>
                </div>
              </div>

              @if (!viewOnly()) {
                <div class="justify-start"><span class="text-datatable-row-color text-xs font-normal font-['Inter']">Select </span><span class="text-datatable-row-color text-xs font-semibold font-['Inter']">Review Full TA</span><span class="text-datatable-row-color text-xs font-normal font-['Inter']"> below to review itinerary, supporting documentation and other details.</span></div>
                <div class="w-full flex justify-center gap-3">
                  <p-button size="small" severity="primary" label="Review Full TA" (onClick)="viewTa()"></p-button>
                  @if (infoContext() === 'home') {
                    @if (canSaveAsTemplate()) {
                      <p-button size="small" severity="primary" label="Save as Template" (onClick)="createTemplate.emit()" data-testid="ta-card-create-template"></p-button>
                    }
                  } @else if (infoContext() === 'compliance') {
                    <!-- The compliance day trips tab only lists TAs with a pending day trip payment -->
                    @if (permissionService.canProcessDayTripPayment()) {
                      <p-button size="small" severity="primary" label="Process Day Trip Payment" (onClick)="actionTa.emit('dayTripPayment')"></p-button>
                    }
                  } @else if (permissionService.canApprove() && selectedTa()?.status === 'PendingApproval') {
                    <p-button size="small" severity="primary" label="Approve TA" (onClick)="actionTa.emit('travelApprove')"></p-button>
                  } @else if (permissionService.canApproveAcquittal() && selectedTa()?.status === 'AcquittalComplete') {
                    <p-button size="small" severity="primary" label="Approve TA Acquittal" (onClick)="actionTa.emit('acquittalApprove')"></p-button>
                  } @else if (permissionService.canApproveCancellation(selectedTa()) && selectedTa()?.status === 'PendingCancelApproval') {
                    <p-button size="small" severity="primary" label="Acknowledge Cancellation" (onClick)="actionTa.emit('cancelApprove')"></p-button>
                  } @else if (permissionService.canFinalise() && selectedTa()?.status === 'AcquittalApproved') {
                    <p-button size="small" severity="primary" label="Finalise TA" (onClick)="actionTa.emit('finalise')"></p-button>
                  } @else if (permissionService.canFinaliseCancellation() && selectedTa()?.status === 'CancelApproved') {
                    <p-button size="small" severity="primary" label="Finalise TA Cancellation" (onClick)="actionTa.emit('cancelFinalise')"></p-button>
                  }
                  @if (infoContext() === 'finance' && permissionService.canProcessPrepayment() && selectedTa()?.status !== 'PendingApproval' && selectedTa()?.isPrePaid && selectedTa()?.prePaymentProcessed == false) {
                    <p-button size="small" severity="primary" label="Process Prepayment" (onClick)="actionTa.emit('prepayment')"></p-button>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="w-full h-px bg-surface-300"></div>
      <!-- bottomGap is everything standing between the chain and the bottom of the window: the 24px
           this card keeps clear of it, this card's own p-5, and the 2rem the comments block keeps
           under the chain. Get it wrong and the card either stops short of the page's edge or
           overruns it into its own scrollbar. -->
      <ta-comments [taId]="taCommentsId()" [capabilities]="taCommentCapabilities()" [bottomGap]="76"></ta-comments>
    </div>
  </div>
}
